import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./react-query/queryClient";
import { Provider } from "react-redux";
import store from "./services/redux/store";
import { PrimeReactProvider } from "primereact/api";
import Tailwind from "primereact/passthrough/tailwind";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  AuthenticationResult,
  EventType,
  PublicClientApplication,
} from "@azure/msal-browser";
import { msalConfig } from "./services/msalAuth/authConfig";
import { MsalProvider } from "@azure/msal-react";
import { CustomProvider } from "rsuite";

export const msalInstance = new PublicClientApplication(msalConfig);

// Benign browser warning from ResizeObserver-based components (charts, tables); not an app error.
const isResizeObserverLoopError = (message: unknown) =>
  typeof message === "string" && message.includes("ResizeObserver loop");

window.addEventListener("error", (event) => {
  if (isResizeObserverLoopError(event.message)) {
    event.stopImmediatePropagation();
  }
});

window.addEventListener("unhandledrejection", (event) => {
  if (isResizeObserverLoopError(event.reason?.message)) {
    event.stopImmediatePropagation();
  }
});

msalInstance.initialize().then(() => {
  // Account selection logic is app dependent. Adjust as needed for different use cases.
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  msalInstance.addEventCallback((event: any) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      const account = payload.account;
      msalInstance.setActiveAccount(account);
    }
  });

  const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
  );
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <PrimeReactProvider value={{ pt: Tailwind }}>
            <CustomProvider>
              <MsalProvider instance={msalInstance}>
                <App pca={msalInstance} />
              </MsalProvider>
            </CustomProvider>
          </PrimeReactProvider>
        </Provider>
        <ReactQueryDevtools />
      </QueryClientProvider>
    </React.StrictMode>,
  );
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
